import * as Sentry from "@sentry/nextjs";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";

import { CreateDNSRecordRequest, CreateDNSRecordResponse } from "api/types";
import { sleep } from "mock-bridge-api/helpers/helpers";
import { useErrorToaster } from "src/common/components/ErrorDetails";
import useBridgeSWR from "src/common/hooks/BridgeSWRClient";
import { client, endpoint } from "src/lib/bridgeClient";
import { SERVER_COMM_ERROR } from "src/lib/error";

export const useWorkflowHealth = (id: string) => {
  return useBridgeSWR<WorkflowHealth>(endpoint.workflowHealth(id));
};

export const fixDns = async (client: Client, req: CreateDNSRecordRequest) => {
  return await client(endpoint.dns, "POST", req);
};

/**
 * `useFixDNS` is a hook that attempts to fix a DNS record for a given instance and Cognito pool.
 * It sends a request to the `fixDns` endpoint with the instance and Cognito pool IDs.
 * After sending the request, it enters a loop where it checks the health of the workflow every `validateInterval` seconds for up to `retryTimes`.
 * If the DNS record exists, it breaks out of the loop.
 * If the DNS record creation is successful, it shows a success toast with the created hostname.
 * If the DNS record creation fails, it shows an error toast with the error message.
 *
 * @param {string} id - The ID of the workflow.
 * @param {number} validateInterval - The interval in seconds to check the health of the workflow. Default is 2 seconds.
 * @param {number} retryTimes - The number of times to retry checking the health of the workflow. Default is 5 times.
 * @returns {Function} - Returns an async function that takes an instance ID and a Cognito pool ID, and returns a Promise that resolves to a `CreateDNSRecordResponse`.
 */
export const useFixDNS = (id: string, validateInterval = 2, retryTimes = 5) => {
  const toastError = useErrorToaster();
  const { mutate } = useSWRConfig();

  return async (instance: string, pool_id: string): Promise<CreateDNSRecordResponse> => {
    try {
      const response = await fixDns(client, {
        instance_id: instance,
        cognito_pool_id: pool_id,
      });

      const data = response.data as CreateDNSRecordResponse;

      // This loop checks the health of the workflow every validate interval for up to validate timeout.
      // It sends a request to the workflow health endpoint and checks if the DNS record exists.
      // If the DNS record exists, it breaks out of the loop. This is to prevent preemptively showing the success toast
      // and enabling the apps button before the DNS record is created.
      for (let i = 0; i < retryTimes; i++) {
        await sleep(validateInterval * 1000);
        const resp = await mutate(endpoint.workflowHealth(id));
        if (resp.master_server.health.dns_record_exists) {
          break;
        }
      }

      toast.success(`Created dns record: ${data.hostname}`);

      return response;
    } catch (error) {
      if (error?.response?.data?.error) {
        toastError("Failed to create dns record", error?.response?.data?.error);
      } else {
        toast.error(`Request failed: ${SERVER_COMM_ERROR}`);
        Sentry.captureException(error);
      }

      return error.response;
    }
  };
};
